import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Divider, Stack, Link, Box, Tab, Button } from '@mui/material';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import { useErrorBoundary } from 'react-error-boundary';
import { Newsletter } from '../../../types/communication/newsletter';
import MailIcon from '@mui/icons-material/Mail';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { set } from 'lodash';

const Newsletters = (): JSX.Element => {
  const theme = useTheme();
  const { showBoundary } = useErrorBoundary();
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);

  const addBorder: React.CSSProperties = {
    borderWidth: 1,
    borderColor: theme.palette.grey[300],
    borderStyle: "solid",
  }

  const getNewsletters = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/newsletters/`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get videos.'));
          }
          return result.json();
        })
        .then(result => {
          setNewsletters(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  useEffect(() => {
    getNewsletters();
  }, []);

  const categorizeNewsletters = (newsletters: Newsletter[]) => {
    return newsletters.reduce(
      (acc, newsletter) => {
        if (newsletter.title.toLowerCase().includes('winter')) {
          acc.winter.push(newsletter);
        } else if (newsletter.title.toLowerCase().includes('summer')) {
          acc.summer.push(newsletter);

        } else if (newsletter.title.toLowerCase().includes('spring')) {
          acc.spring.push(newsletter);

        } else if (newsletter.title.toLowerCase().includes('fall')) {
          acc.fall.push(newsletter);
        }
        else if (newsletter.title.toLowerCase().includes('election')) {
          acc.preelection.push(newsletter);
        }
        else if (newsletter.title.toLowerCase().includes('special newsletter')) {
          acc.special.push(newsletter);
        }
        else {
          acc.otherNewsletters.push(newsletter);
        }
        return acc;
      },
      { special: [] as Newsletter[], preelection: [] as Newsletter[], winter: [] as Newsletter[], summer: [] as Newsletter[], spring: [] as Newsletter[], fall: [] as Newsletter[], otherNewsletters: [] as Newsletter[] }
    );
  };


  const { winter, summer, spring, fall, preelection, special, otherNewsletters } = categorizeNewsletters(newsletters);


  const renderRowsPerYear = () => {
    const rows = [];
    for (let i = 2022; i >= 2000; i--) {
      let w = winter.find((newsletter: Newsletter) => newsletter.title.includes(i.toString()));
      let sp = spring.find((newsletter: Newsletter) => newsletter.title.includes(i.toString()));
      let sr = summer.find((newsletter: Newsletter) => newsletter.title.includes(i.toString()));
      let f = fall.find((newsletter: Newsletter) => newsletter.title.includes(i.toString()));
      rows.push(
        <TableRow key={i}>
          <TableCell style={addBorder} align="center">{i}</TableCell>
          {renderCell(w, 'winter', i)}
          {renderCell(sp, 'spring', i)}
          {renderCell(sr, 'summer', i)}
          {renderCell(f, 'fall', i)}
        </TableRow>
      );
    }
    return rows;
  }

  const renderCell = (newsletter?: Newsletter, season?: string, year?: number) => {
    //blanks
    if (newsletter === undefined) {
      if (year && year === 2006) {
        return (<></>)
      } else if (year && year === 2007 && season === 'spring') {
        return (<></>)
      }
      return (<TableCell style={addBorder} ></TableCell>)
    }

    if (newsletter.id === 3095) {
      return (<TableCell colSpan={2} style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Winter / Spring</Link></TableCell>)
    } else if (season === 'winter') {
      return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Winter</Link></TableCell>)
    } else if (newsletter.id === 3094) {
      return (<TableCell colSpan={2} style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Spring / Summer</Link></TableCell>)
    } else if (season === 'spring') {
      return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Spring</Link></TableCell>)
    } else if (season === 'summer') {
      //summer exceptions
      if (year) {
        if (year === 2021) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link> / <Link href='' target='_blank'>Special</Link></TableCell>)
        if (year === 2020) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link> / <Link href={special.find((v : Newsletter) => v.id === 3034)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2019) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link> / <Link href={preelection.find((v : Newsletter) => v.id === 3039)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2018) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link> / <Link href={preelection.find((v : Newsletter) => v.id === 3045)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2017) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link> / <Link href={preelection.find((v : Newsletter) => v.id === 3051)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2016) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link> / <Link href={special.find((v : Newsletter) => v.id === 3057)?.url} target='_blank'>Special</Link></TableCell>)
      }
      return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Summer</Link></TableCell>)
    } else if (season === 'fall') {
      //fall exceptions
      if (year) {
        if (year === 2019) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Fall</Link> / <Link href={special.find((v : Newsletter) => v.id === 3041)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2016) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Fall</Link> / <Link href={preelection.find((v : Newsletter) => v.id === 3059)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2014) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Fall</Link> / <Link href={special.find((v : Newsletter) => v.id === 3068)?.url} target='_blank'>Special</Link></TableCell>)
        if (year === 2009) return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Fall</Link> / <Link href={special.find((v : Newsletter) => v.id === 3089)?.url} target='_blank'>Special</Link></TableCell>)
      }
      return (<TableCell style={addBorder} align="center"><Link href={newsletter.url} target='_blank'>Fall</Link></TableCell>)
    }
  }

  const renderCurrentNewsletters = () => {
    const rows = [];

    for (let i = 0; i < otherNewsletters.length; i = i + 4) {
      rows.push(
        <TableRow key={i}>
          <TableCell style={addBorder} align="center" ><Link href={otherNewsletters[i]?.url} target='_blank'>{otherNewsletters[i]?.title}</Link></TableCell>
          <TableCell style={addBorder} align="center">{otherNewsletters[i + 1] && <Link href={otherNewsletters[i + 1].url} target='_blank'>{otherNewsletters[i + 1].title}</Link>}</TableCell>
          <TableCell style={addBorder} align="center">{otherNewsletters[i + 2] && <Link href={otherNewsletters[i + 2].url} target='_blank'>{otherNewsletters[i + 2].title}</Link>}</TableCell>
          <TableCell style={addBorder} align="center">{otherNewsletters[i + 3] && <Link href={otherNewsletters[i + 3].url} target='_blank'>{otherNewsletters[i + 3].title}</Link>}</TableCell>
        </TableRow>
      );
    }
    return rows;

  }




  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          gutterBottom
          marginBottom={2}
        >
          OCPF Newsletters
        </Typography>
        <Divider />
        <Button href='https://visitor.constantcontact.com/manage/optin/ea?v=001Nz3Ls26PyJcuVB7EQ-UbFIw3NpIJNWlWt-Yy4RDOu3b8iQdp_aV_OzwqrwF39p3vwlwshLX0uJmpQ_ZqExAeEZJ-TT_OzCue' target='_blank' startIcon={<MailIcon />}>Subscribe to OCPF's Newsletter</Button>

        <TableContainer >
          <Table sx={{ minWidth: 650, marginBottom: 3 }} size="small" >
            <TableHead>
              <TableRow>
                <TableCell align="center" colSpan={5} style={addBorder}><Typography variant='h6'><b>OCPF Monthly Newsletter</b></Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderCurrentNewsletters().map((rows: JSX.Element) => rows)}
            </TableBody>
          </Table>
        </TableContainer>


        <TableContainer>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" colSpan={5}  style={addBorder}><Typography variant='h6'><b>OCPF Quarterly Newsletter (until April 2022)</b></Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderRowsPerYear().map((rows: JSX.Element) => rows)}
            </TableBody>

          </Table>
        </TableContainer>

      </Container>
    </Main>
  );
};

export default Newsletters;